import React, { useMemo } from 'react';
import { FloatingUserCell } from "./FloatingUserCell";
import {
  BorderDayCell,
  PROJECT_ROW_HEIGHT,
  WallPlannerRow,
  WEEK_WIDTH,
  WeekCell
} from '../WallPlannerStructuralComponents';
import styled, { css } from "styled-components";
import { BodyRegular, BodyVerySmall } from '@shared/ui/atoms/fonts/Body';
import { useSelector } from "react-redux";
import { selectTeamProjects } from "../wallPlanner.duck";
import { daysBetween } from '@shared/utils/DateUtils';
import { TeamEvent } from '@features/wall-planner/wall-planner.models';
import { smallTablet } from '@shared/ui/atoms/MediaQueries';
import { WithWeeksInCurrentCalendarMonth } from '@features/whereabouts/hooks/WithWeekInCurrentCalendarMonth';
import { useTranslation } from "react-i18next";

interface Props {
  team: any
}

export function WallPlannerProjectRow(props: Props) {
  const weeksInMonth = WithWeeksInCurrentCalendarMonth();
  const allProjects = useSelector(selectTeamProjects);
  const {t} = useTranslation();
  const teamProjects = useMemo(() => allProjects.filter((teamEvent: TeamEvent) => teamEvent.teamId === props.team.id) ?? [], [props.team.id, allProjects]);

  // Only consider projects that intersect the visible month range; bars are clamped to this range so
  // nothing is drawn before the first day or past the last day shown.
  const visibleProjects = useMemo(() => {
    if (!weeksInMonth || weeksInMonth.length === 0) return [];
    const rangeStart = weeksInMonth[0];
    const rangeEnd = weeksInMonth[weeksInMonth.length - 1].clone().add(6, 'days');
    return teamProjects.filter((p: TeamEvent) => !p.from.isAfter(rangeEnd, 'day') && !p.to.isBefore(rangeStart, 'day'));
  }, [teamProjects, weeksInMonth]);

  // Pack projects into lanes so they only stack when their date ranges actually overlap. Greedy
  // interval partitioning: process by start date, drop each project into the first lane whose last
  // project ends before this one starts (adjacent, non-overlapping projects share a lane).
  const { laneByProjectId, laneCount } = useMemo(() => {
    const sorted = [...visibleProjects].sort((a, b) => a.from.valueOf() - b.from.valueOf() || a.to.valueOf() - b.to.valueOf());
    const laneEnds: TeamEvent['to'][] = [];
    const laneByProjectId = new Map<number, number>();
    sorted.forEach(project => {
      let lane = laneEnds.findIndex(end => end.isBefore(project.from, 'day'));
      if (lane === -1) lane = laneEnds.length;
      laneEnds[lane] = project.to;
      laneByProjectId.set(project.id, lane);
    });
    return { laneByProjectId, laneCount: laneEnds.length };
  }, [visibleProjects]);

  if (!weeksInMonth || weeksInMonth.length === 0) {
    return null;
  }
  const firstDay = weeksInMonth[0];
  const lastDay = weeksInMonth[weeksInMonth.length - 1].clone().add(6, 'days');

  const onProjectEventClicked = (projectEvent: TeamEvent) => {
    window.location.href = `/whereabouts/work-planner/event/${projectEvent.id}`;
  }

  return (
    <WallPlannerProjectRowWrapper weekCount={weeksInMonth.length} projectCount={laneCount}>
      <FloatingUserCell>
        <BodyRegular weight={400} style={{alignSelf: 'flex-start'}}>{t('work-planner.events-and-projects')}</BodyRegular>
      </FloatingUserCell>
      {weeksInMonth.map((timeFrame, k) => (
        <WeekCell key={k}>
          <BorderDayCell/>
          <BorderDayCell/>
          <BorderDayCell/>
          <BorderDayCell/>
          <BorderDayCell/>
          <BorderDayCell weekend={true}/>
          <BorderDayCell weekend={true}/>
        </WeekCell>
      ))}
      <ProjectActivitiesContainer projectCount={laneCount}>
        <ProjectContainer style={{height: laneCount * 28}}>
          {visibleProjects.map((project: TeamEvent, key: number) => {
            // Clamp the bar to the visible range so it never extends before firstDay or past lastDay.
            const clampedFrom = project.from.isBefore(firstDay, 'day') ? firstDay : project.from;
            const clampedTo = project.to.isAfter(lastDay, 'day') ? lastDay : project.to;
            return (
              <ProjectFill key={key} heightOffset={laneByProjectId.get(project.id) ?? 0}
                           start={daysBetween(clampedFrom, firstDay)}
                           days={daysBetween(clampedTo, clampedFrom) + 1}
                           title={project.eventName}
                           onClick={() => onProjectEventClicked(project)}>
                <BodyVerySmall weight={600}>{project.eventName}</BodyVerySmall>
              </ProjectFill>
            );
          })}
        </ProjectContainer>
      </ProjectActivitiesContainer>
    </WallPlannerProjectRowWrapper>
  )
}

const WallPlannerProjectRowWrapper = styled(WallPlannerRow)<any>`
  height: ${props => (props.projectCount ?? 1) * PROJECT_ROW_HEIGHT + 50}px;
  width: 100%;
  .floatingUser__userColumn {
    min-height: 40px;
  }
  @media (${smallTablet}) {
    height: ${props => Math.max((props.projectCount ?? 1), 1) * PROJECT_ROW_HEIGHT}px;
    // Keep the left user column (names/avatars) above the absolutely-positioned project bars so
    // early-month bars slide underneath it rather than covering it.
    .userColumn__wrapper {
      position: relative;
      z-index: 2;
    }
  }
`

const ProjectContainer = styled.div`
`


const ProjectActivitiesContainer = styled.div<any>`
  position: absolute;
  left: -${WEEK_WIDTH}px;
  top: 40px;
  @media (${smallTablet}) {
    left: 0;
    top: 0;
  }
`

const ProjectFill = styled.div<any>`
  position: absolute;
  height: 24px;
  align-self: center;
  background: #E7FCFF;
  border: 1px solid #1EE1FF;
  border-radius: 4px;
  transition: background .25s;
  display: flex;
  align-items: center;
  user-select: none;
  top: 4px;
  padding: 2px 8px;
  cursor: pointer;
  ${props => css`left: ${props.start ? (props.start * (WEEK_WIDTH / 7) + 2) + WEEK_WIDTH + 'px' : (WEEK_WIDTH + 2) + 'px'};`};
  ${props => css`top: ${props.heightOffset * 30}px`};
  ${props => css`width: ${props.days ? (props.days * (WEEK_WIDTH / 7) - 6) + 'px' : 0};`};
  p {
    overflow: hidden;
    white-space: nowrap;
    max-width: 90%;
    text-overflow: ellipsis;
  }
`
